/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2016 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/

/**
 * Simple singleton style container so all Stock related stuff has a centralized point.
 */
var iaw = iaw || { };

iaw.stockUtil = {
	STOCK_NAMESPACE: 'adobestock',
	STOCK_DATA_KEY: 'trackingdata',
	STOCK_STATE_PURCHASED: 'purchased',
	STOCK_STATE_NOT_PURCHASED: 'not_purchased',

	STOCK_URL_LICENSE: 'https://www.adobe.com/go/stocklicense_',
	STOCK_URL: 'https://stock.adobe.com',
	STOCK_URL_SEARCH: 'https://stock.adobe.com/Search',
	STOCK_URL_STAGE: 'https://primary.stock.stage.adobe.com',
	STOCK_URL_SEARCH_STAGE: 'https://primary.stock.stage.adobe.com/Search',

	/**
	 * Get Stock data from representation.
	 *
	 * @param [Object] representation	representation of cc library element
	 * @return [Object] Stock data
	 */
	getStockData: function(representation) {
		return representation && representation.getValue(this.STOCK_NAMESPACE, this.STOCK_DATA_KEY);
	},

	/**
	 * Get Stock data from cc library element.
	 *
	 * @param [Object] element 	cc library element
	 * @param [Number] index 	index of representation in cc library element
	 * @return [Object] Stock data
	 */
	getStockDataForElement: function(element, index) {
		// First try the primary representation
		var representation = index === undefined ? element.getPrimaryRepresentation() : element.representations[index];
		var stockData = this.getStockData(representation);

		if (stockData && representation.isExternalLink && representation.getCachedExternalLinkError()) {
			// It's an external link, but the external link isn't valid: Try the next representation
			return iaw.stockUtil.getStockDataForElement(element, index === undefined ? 0 : index + 1);
		}

		return stockData;
	},

	/**
	 * Get Stock Id from cc library element.
	 *
	 * @param [Object] element 	cc library element
	 * @return [String] Stock Id
	 */
	getElementStockId: function(element) {
		var stockData = this.getStockDataForElement(element);
		if (stockData) {
			return Number(stockData.content_id);
		}
		return undefined;
	},

	/**
	 * Get Stock license status from cc library element.
	 *
	 * @param [Object] element 	cc library element
	 * @return [String] Stock license status
	 */
	getElementStockLicense: function(element) {
		var stockData = this.getStockDataForElement(element);
		if (stockData) {
			if (stockData.state === this.STOCK_STATE_PURCHASED) {
				return String(stockData.license);
			}
		}
		return undefined;
	},

	/**
	 * License Stock template.
	 *
	 * @param [String] imageId 		content Id of template
	 * @param [String] licenseType 	license type of template
	 * @return [Promise] promise that will resolve the result to license template
	 */
	licenseTemplate: function(imageId, licenseType) {
		return new Promise(function(resolve, reject) {
			iaw.cepUtil.setEnvironment(iaw.cepUtil.usingStageAuthentication() ? 'stage' : 'prod');
			iaw.cepUtil.getIMSAccessToken(function(token) {
				if (!token) {
					reject('Invalid access token.');
					return;
				}
				var url = iaw.cepUtil.usingStageAuthentication() ? 'https://stock-stage.adobe.io/Rest/Libraries/1/Content/License?' : 'https://stock.adobe.ioRest/Libraries/1/Content/License?';
				url += 'content_id=' + imageId + '&license=' + licenseType;
				iaw.util.promise('GET', url, token).then(function(res) {
					if (typeof res === 'string') {
						res = JSON.parse(res);
					}
					resolve(res);
				}).catch(function(err) {
					reject(err);
				});
			});
		});
	},

	/**
	 * Create the URL to open/use for the selected Adobe Stock template.
	 * https://wiki.corp.adobe.com/display/adobestock/Jump+to+Adobe+Stock+Search+Pages
	 *
	 * @param [String] hostID	phxs, ilst, etc.
	 * @param [String] type     template type
	 * @param [String] term     search term, if any
	 * @return [String] containing the URL that was opened
	 */
	composeStockLink: function( hostID, type, term ) {
		var baseURL = iaw.cepUtil.usingStageAuthentication() ? this.STOCK_URL_SEARCH_STAGE : this.STOCK_URL_SEARCH;
		var category = -1;
		var application = 0;

		term = typeof term === 'undefined' ? '' : term;

		switch (type) {
			case 'recent':
			case 'saved':
				category = 0;
				break;
			case 'mobile':
				category = 1;
				break;
			case 'web':
				category = 2;
				break;
			case 'print':
				category = 3;
				break;
			case 'photo':
				category = 4;
				break;
			case 'film':
				category = 5;
				break;
			case 'art':
				category = 6;
				break;
			default:
				category = -1; // signify error
				break;
		}

		switch (hostID) {
			case 'PHXS':
				application = 1;
				break;
			case 'ILST':
				application = 2;
				break;
			case 'IDSN':
				application = 3;
				break;
			default:
				application = 0;
				break;
		}

		term = term ? 'k=' + iaw.util.fixedEncodeURIComponent(term) + '&' : '';
		term += iaw.analytics.getAnalyticsQueryString() + '&'; // includes language and other host data

		var query = 'filters[content_type:template]=1&filters[template_type_id][]=' + application;
		// in order to search all categories, remove the filter
		var cat = category === 0 ? '' : '&filters[template_category_id][]=' + category;
		var analyticsQuery = '&as_channel=adobe_apps&as_source=app&as_campclass=brand&as_campaign=templates_' + hostID;
		var featureFlag = 'ff_4815162342=true&';

		return (baseURL + '?' + featureFlag + term + query + cat + analyticsQuery);
	},
	
	/**
	 * Open Adobe Stock URL given the product and template type
	 * https://wiki.corp.adobe.com/display/adobestock/Jump+to+Adobe+Stock+Search+Pages
	 *
	 * @param [String] hostID	phxs, ilst, etc.
	 * @param [String] type     template type
	 * @param [String] term     search term, if any
	 * @return [String] containing the URL that was opened
	 */
	openStockLink: function( hostID, type, term ) {
		var uri = this.composeStockLink(hostID, type, term);

		if (window.__adobe_cep__) {
			iaw.util.openDefaultBrowserAuthenicated('adobeStock', uri);
		}
		else {
			window.open(uri);
		}
		return uri;
	}
};
